import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  schedulesApi,
  sellersApi,
  type ScheduleInput,
  type ScheduleWindow,
  type ScheduleOverride,
  type SchedulePreview,
  type SellerListItem,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { WEEKDAYS_RU, fmtMskHuman } from '../lib/weekdays';
import { useBasePath, withBase } from '../lib/basePath';

// ── Redesigned schedule editor ────────────────────────────────
// «Регулярные поставки» — weekly pattern (ship weekdays + time) with a
// day-based deadline («за N дней до отгрузки» at its own time, or «без
// дедлайна»). A dated week strip shows real dates, scrolls week-by-week, and
// lets the operator click a shipment to move it / change or drop its deadline /
// remove it for that week — stored as per-occurrence overrides on top of the
// pattern (the pattern still repeats forever).
// «Конкретные даты» — an explicit list of one-off (ship, deadline) datetimes.

type Mode = 'regular' | 'dates';
type DeadlineKind = 'days' | 'none';

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
function pad2(n: number) { return String(n).padStart(2, '0'); }
function isoWeekday(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return wd === 0 ? 7 : wd;
}
function addDays(ymd: string, delta: number) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}
function mondayOf(ymd: string) { return addDays(ymd, -(isoWeekday(ymd) - 1)); }
function mskTodayYmd() { return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(new Date()); }
function fmtDateShort(ymd: string) { const [, m, d] = ymd.split('-').map(Number); return `${d} ${MONTHS[m - 1]}`; }
// 'YYYY-MM-DDTHH:MM' → 'd мес HH:MM'
function fmtDtShort(dt: string) {
  const [date, time] = dt.split('T');
  return `${fmtDateShort(date)} ${(time || '').slice(0, 5)}`;
}
function timePart(dt: string) { return (dt.split('T')[1] || '').slice(0, 5); }
function datePart(dt: string) { return dt.split('T')[0]; }

export default function ScheduleEdit() {
  const { id: idStr } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const base = useBasePath();
  const isNew = !idStr || idStr === 'new';
  const id = isNew ? null : Number(idStr);

  const [name, setName] = useState('');
  const [sellerId, setSellerId] = useState<number | null>(null);
  const [sellers, setSellers] = useState<SellerListItem[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [notes, setNotes] = useState('');

  const [mode, setMode] = useState<Mode>('regular');
  const [shipDays, setShipDays] = useState<Set<number>>(new Set());
  const [shipTime, setShipTime] = useState('10:00');
  const [deadlineKind, setDeadlineKind] = useState<DeadlineKind>('days');
  const [deadlineDays, setDeadlineDays] = useState(1);
  const [deadlineTime, setDeadlineTime] = useState('18:00');
  const [overrides, setOverrides] = useState<ScheduleOverride[]>([]);
  const [windows, setWindows] = useState<ScheduleWindow[]>([]);

  const [viewMonday, setViewMonday] = useState(() => mondayOf(mskTodayYmd()));
  const [selBase, setSelBase] = useState<string | null>(null);
  const [edShip, setEdShip] = useState('');
  const [edHasDl, setEdHasDl] = useState(true);
  const [edDl, setEdDl] = useState('');

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<SchedulePreview | null>(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      sellersApi.list().then((r) => {
        setSellers(r.sellers);
        if (isNew && r.sellers.length === 1) setSellerId(r.sellers[0].id);
      }).catch(() => { /* noop */ });
    } else if (user) setSellerId(user.id);
  }, [user, isNew]);

  useEffect(() => {
    if (isNew || !id) return;
    schedulesApi.get(id).then((r) => {
      const s = r.schedule;
      setName(s.name); setSellerId(s.seller_id); setIsDefault(s.is_default); setNotes(s.notes ?? '');
      setShipDays(new Set(s.ship_days)); setShipTime(s.ship_time_msk);
      setOverrides(s.window_overrides ?? []);
      setWindows((s.windows ?? []).map((w) => ({
        ship_dt: w.ship_dt.slice(0, 16),
        deadline_dt: w.deadline_dt ? w.deadline_dt.slice(0, 16) : null,
      })));
      setMode((s.windows ?? []).length > 0 ? 'dates' : 'regular');
      // Deadline → day-based model.
      if (s.deadline_days_before != null) {
        setDeadlineKind('days'); setDeadlineDays(s.deadline_days_before); setDeadlineTime(s.deadline_time_msk || '18:00');
      } else if (s.deadline_anchor === 'relative' && s.deadline_offset_h > 0) {
        setDeadlineKind('days'); setDeadlineDays(Math.max(1, Math.round(s.deadline_offset_h / 24))); setDeadlineTime(s.deadline_time_msk || '18:00');
      } else if (s.deadline_anchor === 'absolute' && s.deadline_days.length > 0) {
        setDeadlineKind('days'); setDeadlineDays(1); setDeadlineTime(s.deadline_time_msk || '18:00');
      } else {
        setDeadlineKind('none');
      }
    }).catch((e) => setErr(String(e?.message ?? e))).finally(() => setLoading(false));
  }, [id, isNew]);

  const previewKey = [
    mode, [...shipDays].sort().join(','), shipTime, deadlineKind, deadlineDays, deadlineTime,
    JSON.stringify(overrides), windows.map((w) => `${w.ship_dt}/${w.deadline_dt ?? ''}`).join(';'),
  ].join('|');
  useEffect(() => {
    if (isNew || !id) return;
    schedulesApi.preview(id, 6).then(setPreview).catch(() => { /* best-effort */ });
  }, [id, isNew, previewKey]);

  function toggleShip(day: number) {
    setShipDays((prev) => { const n = new Set(prev); n.has(day) ? n.delete(day) : n.add(day); return n; });
  }

  // ── deadline / occurrence helpers ──
  function patternDeadline(baseYmd: string): string | null {
    if (deadlineKind !== 'days') return null;
    return `${addDays(baseYmd, -deadlineDays)}T${deadlineTime}`;
  }
  function effectiveOcc(baseYmd: string) {
    const ov = overrides.find((o) => o.base_date === baseYmd) || null;
    const skipped = ov?.action === 'skip';
    const ship = (ov?.action === 'edit' && ov.ship_dt) ? ov.ship_dt : `${baseYmd}T${shipTime}`;
    let deadline: string | null;
    if (skipped) deadline = null;
    else if (ov?.action === 'edit') deadline = ov.deadline_cleared ? null : (ov.deadline_dt ?? patternDeadline(baseYmd));
    else deadline = patternDeadline(baseYmd);
    return { ov, skipped, ship, deadline };
  }
  function upsertOverride(o: ScheduleOverride) {
    setOverrides((prev) => [...prev.filter((x) => x.base_date !== o.base_date), o]);
  }
  function removeOverride(baseYmd: string) {
    setOverrides((prev) => prev.filter((x) => x.base_date !== baseYmd));
  }

  function openEditor(baseYmd: string) {
    const e = effectiveOcc(baseYmd);
    setSelBase(baseYmd);
    setEdShip(e.skipped ? `${baseYmd}T${shipTime}` : e.ship);
    setEdHasDl(!!e.deadline);
    setEdDl(e.deadline ?? `${addDays(baseYmd, -Math.max(1, deadlineDays))}T${deadlineTime}`);
  }
  function saveOcc() {
    if (!selBase) return;
    upsertOverride({
      base_date: selBase, action: 'edit',
      ship_dt: edShip,
      deadline_cleared: !edHasDl,
      deadline_dt: edHasDl ? edDl : null,
    });
    setSelBase(null);
  }
  function skipOcc() { if (selBase) { upsertOverride({ base_date: selBase, action: 'skip' }); setSelBase(null); } }
  function resetOcc() { if (selBase) { removeOverride(selBase); setSelBase(null); } }

  function buildInput(): Omit<ScheduleInput, 'seller_id'> {
    const baseFields = {
      name: name.trim(),
      ship_days: [...shipDays].sort((a, b) => a - b),
      ship_time_msk: shipTime,
      is_default: isDefault,
      notes: notes.trim() || null,
    };
    if (mode === 'dates') {
      return {
        ...baseFields,
        windows: windows.filter((w) => w.ship_dt).map((w) => ({ ship_dt: w.ship_dt, deadline_dt: w.deadline_dt || null }))
          .sort((a, b) => a.ship_dt.localeCompare(b.ship_dt)),
        window_overrides: [],
        deadline_anchor: 'relative', deadline_offset_h: 0, deadline_days: [], deadline_time_msk: '23:59', deadline_days_before: null,
      };
    }
    return {
      ...baseFields, windows: [],
      window_overrides: overrides,
      deadline_anchor: 'relative', deadline_offset_h: 0, deadline_days: [],
      deadline_time_msk: deadlineTime,
      deadline_days_before: deadlineKind === 'days' ? deadlineDays : null,
    };
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setSavedMsg(null);
    try {
      if (mode === 'regular' && shipDays.size === 0) throw new Error('Выберите хотя бы один день отгрузки');
      if (mode === 'dates' && windows.filter((w) => w.ship_dt).length === 0) throw new Error('Добавьте хотя бы одну дату отгрузки');
      if (isNew) {
        if (!sellerId) throw new Error('Не выбран продавец');
        const r = await schedulesApi.create({ ...buildInput(), seller_id: sellerId });
        nav(withBase(base, `/schedules/${r.schedule.id}`), { replace: true });
      } else if (id) {
        await schedulesApi.update(id, buildInput());
        setSavedMsg('Сохранено ✓'); setTimeout(() => setSavedMsg(null), 2200);
      }
    } catch (e) { setErr(String((e as Error)?.message ?? e)); } finally { setBusy(false); }
  }

  async function onDelete() {
    if (!id) return;
    if (!confirm('Удалить расписание? Товары, которые на него ссылаются, останутся без расписания.')) return;
    await schedulesApi.remove(id);
    nav(withBase(base, '/schedules'), { replace: true });
  }

  function addWindow() {
    const today = mskTodayYmd();
    const ship = `${addDays(today, 7)}T10:00`;
    const deadline = `${addDays(today, 6)}T18:00`;
    setWindows((ws) => [...ws, { ship_dt: ship, deadline_dt: deadline }]);
  }

  if (loading) return <div className="container"><p className="muted">Загрузка…</p></div>;

  const todayYmd = mskTodayYmd();
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(viewMonday, i));
  const overrideCount = overrides.length;

  return (
    <div className="container narrow">
      <div className="page-head">
        <h2>{isNew ? 'Новое расписание' : `Расписание #${id}`}</h2>
        <div className="row">
          <Link to={withBase(base, '/schedules')} className="btn">← К списку</Link>
          {!isNew && <button className="btn danger" onClick={onDelete} type="button">Удалить</button>}
        </div>
      </div>

      <form className="card form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {isNew && user?.role === 'admin' && (
          <label>
            <span>Продавец *</span>
            <select value={sellerId ?? ''} onChange={(e) => setSellerId(Number(e.target.value))} required>
              <option value="">— выберите —</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.login}</option>)}
            </select>
          </label>
        )}

        <label>
          <span>Название *</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder='Например: «Розы — Пн/Чт»' required />
        </label>

        <div>
          <div className="muted" style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--ink-2)' }}>Тип расписания</div>
          <div className="seg">
            <button type="button" className={mode === 'regular' ? 'on' : ''} onClick={() => setMode('regular')}>Регулярные поставки</button>
            <button type="button" className={mode === 'dates' ? 'on' : ''} onClick={() => setMode('dates')}>Конкретные даты</button>
          </div>
        </div>

        {mode === 'regular' ? (
          <>
            <fieldset className="fieldset">
              <legend>Дни отгрузки</legend>
              <div className="wd-pick">
                {WEEKDAYS_RU.map((w) => (
                  <button key={w.iso} type="button" className={shipDays.has(w.iso) ? 'on' : ''} onClick={() => toggleShip(w.iso)}>{w.short}</button>
                ))}
              </div>
              <label style={{ marginTop: 12, maxWidth: 200 }}>
                <span>Время отгрузки (МСК)</span>
                <input type="time" value={shipTime} onChange={(e) => setShipTime(e.target.value)} required />
              </label>
            </fieldset>

            <fieldset className="fieldset">
              <legend>Приём заказов (дедлайн)</legend>
              <div className="seg">
                <button type="button" className={deadlineKind === 'days' ? 'on' : ''} onClick={() => setDeadlineKind('days')}>За N дней до отгрузки</button>
                <button type="button" className={deadlineKind === 'none' ? 'on' : ''} onClick={() => setDeadlineKind('none')}>Без дедлайна</button>
              </div>
              {deadlineKind === 'days' && (
                <div className="occ-row" style={{ marginTop: 12 }}>
                  <label style={{ maxWidth: 170 }}>
                    <span>За сколько дней</span>
                    <input type="number" min={0} max={60} value={deadlineDays}
                      onChange={(e) => setDeadlineDays(Math.max(0, Math.min(60, Number(e.target.value) || 0)))} />
                  </label>
                  <label style={{ maxWidth: 160 }}>
                    <span>Время дедлайна (МСК)</span>
                    <input type="time" value={deadlineTime} onChange={(e) => setDeadlineTime(e.target.value)} />
                  </label>
                </div>
              )}
              {deadlineKind === 'none' && (
                <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>Заказы принимаются вплоть до момента отгрузки.</p>
              )}
            </fieldset>

            {/* Dated week strip */}
            <div>
              <div className="muted" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
                График поставок{overrideCount > 0 ? ` · изменённых окон: ${overrideCount}` : ''}
              </div>
              <div className="wk-nav">
                <button type="button" title="Предыдущая неделя" onClick={() => { setViewMonday((m) => addDays(m, -7)); setSelBase(null); }}>‹</button>
                <span className="wk-range">{fmtDateShort(viewMonday)} – {fmtDateShort(addDays(viewMonday, 6))}</span>
                <button type="button" title="Следующая неделя" onClick={() => { setViewMonday((m) => addDays(m, 7)); setSelBase(null); }}>›</button>
                <button type="button" className="wk-today" onClick={() => { setViewMonday(mondayOf(todayYmd)); setSelBase(null); }}>Сегодня</button>
              </div>
              <div className="wk-grid">
                {weekDays.map((ymd, i) => {
                  const iso = i + 1;
                  const isShip = shipDays.has(iso);
                  const isToday = ymd === todayYmd;
                  if (!isShip) {
                    return (
                      <div key={ymd} className={'wk-day muted-day' + (isToday ? ' is-today' : '')}>
                        <div className="wk-dhead"><span className="wk-dow">{WEEKDAYS_RU[i].short}</span><span className="wk-date">{Number(ymd.split('-')[2])}</span></div>
                      </div>
                    );
                  }
                  const occ = effectiveOcc(ymd);
                  const shipMoved = datePart(occ.ship) !== ymd;
                  return (
                    <div key={ymd} className={'wk-day ship' + (isToday ? ' is-today' : '') + (occ.skipped ? ' skipped' : '')}
                      onClick={() => openEditor(ymd)} title="Нажмите, чтобы изменить это окно">
                      <div className="wk-dhead"><span className="wk-dow">{WEEKDAYS_RU[i].short}</span><span className="wk-date">{Number(ymd.split('-')[2])}</span></div>
                      {occ.skipped ? (
                        <span className="wk-skip-tag">снято</span>
                      ) : (
                        <>
                          <span className="wk-ship-chip">📦 {shipMoved ? fmtDtShort(occ.ship) : timePart(occ.ship)}</span>
                          <span className="wk-conn">↑</span>
                          {occ.deadline
                            ? <span className="wk-dead-chip">до {fmtDtShort(occ.deadline)}</span>
                            : <span className="wk-dead-none">без дедлайна</span>}
                          {occ.ov ? <span className="wk-edited">● изменено</span> : null}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="wk-legend">
                <span><span className="dot" style={{ background: 'var(--accent)' }}></span>отгрузка</span>
                <span><span className="dot" style={{ background: 'var(--amber)' }}></span>дедлайн приёма</span>
              </div>
              <p className="wk-hint">Паттерн повторяется каждую неделю. Нажмите на день отгрузки, чтобы сдвинуть его, изменить дедлайн или снять только эту поставку.</p>

              {selBase && (
                <div className="occ-editor">
                  <h4>Окно {fmtDateShort(selBase)} ({WEEKDAYS_RU[isoWeekday(selBase) - 1].short})</h4>
                  <div className="occ-row">
                    <label style={{ maxWidth: 230 }}>
                      <span>Отгрузка (МСК)</span>
                      <input type="datetime-local" value={edShip} onChange={(e) => setEdShip(e.target.value)} />
                    </label>
                    <label className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <input type="checkbox" checked={edHasDl} onChange={(e) => setEdHasDl(e.target.checked)} />
                      <span>Есть дедлайн</span>
                    </label>
                    {edHasDl && (
                      <label style={{ maxWidth: 230 }}>
                        <span>Дедлайн приёма (МСК)</span>
                        <input type="datetime-local" value={edDl} onChange={(e) => setEdDl(e.target.value)} />
                      </label>
                    )}
                  </div>
                  <div className="occ-actions">
                    <button type="button" className="btn primary" onClick={saveOcc}>Сохранить окно</button>
                    <button type="button" className="btn danger" onClick={skipOcc}>Снять эту отгрузку</button>
                    {overrides.some((o) => o.base_date === selBase) && (
                      <button type="button" className="btn" onClick={resetOcc}>Вернуть к расписанию</button>
                    )}
                    <button type="button" className="btn ghost" onClick={() => setSelBase(null)}>Отмена</button>
                  </div>
                  <p className="muted" style={{ fontSize: 12, marginBottom: 0, marginTop: 8 }}>
                    Изменение применится после кнопки «Сохранить» внизу формы.
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <fieldset className="fieldset">
            <legend>Конкретные даты отгрузок</legend>
            <p className="muted" style={{ marginTop: 0 }}>
              Список точных дат. Для каждой — момент закрытия приёма заказов (можно оставить пустым). Время — МСК.
            </p>
            {windows.length === 0 && <p className="muted">Пока нет дат. Нажмите «+ Добавить дату».</p>}
            {windows.map((w, idx) => (
              <div className="row" key={idx} style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={{ flex: 1, minWidth: 200 }}>
                  <span>Отгрузка (МСК)</span>
                  <input type="datetime-local" value={w.ship_dt} required
                    onChange={(e) => setWindows((ws) => ws.map((x, i) => i === idx ? { ...x, ship_dt: e.target.value } : x))} />
                </label>
                <label style={{ flex: 1, minWidth: 200 }}>
                  <span>Дедлайн приёма (необяз.)</span>
                  <input type="datetime-local" value={w.deadline_dt ?? ''}
                    onChange={(e) => setWindows((ws) => ws.map((x, i) => i === idx ? { ...x, deadline_dt: e.target.value || null } : x))} />
                </label>
                <button type="button" className="btn" onClick={() => setWindows((ws) => ws.filter((_, i) => i !== idx))}>✕</button>
              </div>
            ))}
            <div className="row" style={{ marginTop: 4 }}>
              <button type="button" className="btn" onClick={addWindow}>+ Добавить дату</button>
            </div>
          </fieldset>
        )}

        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          <span>Использовать по умолчанию для этого продавца</span>
        </label>

        <label>
          <span>Заметки</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {err && <div className="error">{err}</div>}

        <div className="row" style={{ gap: 12 }}>
          <button className="btn primary" disabled={busy} type="submit">{busy ? 'Сохраняем…' : (isNew ? 'Создать' : 'Сохранить')}</button>
          {savedMsg && <span className="ok-msg">{savedMsg}</span>}
        </div>
      </form>

      {preview && (
        <div className="card sub" style={{ marginTop: 18 }}>
          <h3>Ближайшие окна (от {fmtMskHuman(preview.from)})</h3>
          {preview.windows.length === 0 ? (
            <p className="muted">Не удалось рассчитать — проверьте, что выбран хотя бы один день отгрузки или добавлена дата.</p>
          ) : (
            <table className="grid compact">
              <thead><tr><th>#</th><th>Приём заказов до</th><th>Отгрузка</th></tr></thead>
              <tbody>
                {preview.windows.map((w, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{w.deadline ? fmtMskHuman(w.deadline) : <span className="muted">без дедлайна</span>}</td>
                    <td>{fmtMskHuman(w.ship)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
